import { type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { autocompleteClasses, createFilterOptions } from "@mui/material/Autocomplete";
import { Stack, Typography, Divider } from "@mui/material";
import { CircularProgress } from "@privacy/ui";
import type { SxProps, Theme } from "@mui/material/styles";
import type { Translate } from "@privacy/translations";
import type { IconProps } from "./icons";

export const AUTOCOMPLETE_LOADING_OPTION: AutocompleteOptionType = {
  label: "LOADING",
  value: "LOADING",
  description: "LOADING",
};

export type AutocompleteOptionType = { label: string; value: string; description?: string };
export type AutocompleteOptionWithMaybeIsNew = AutocompleteOptionType & { isNew?: boolean };

export const userToAutocompleteOption = ({
  userId,
  firstName,
  lastName,
  email,
}: {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}) => ({ value: userId, label: `${firstName} ${lastName}`, description: email });

type AutocompleteOptionProps = {
  option: AutocompleteOptionType;
  dense?: boolean;
  icon?: ReactElement<IconProps>;
  endAdornment?: ReactNode;
  sx?: SxProps<Theme>;
} & HTMLAttributes<HTMLLIElement>;

export const AutocompleteOption = ({ option, dense, icon, endAdornment, sx, ...rest }: AutocompleteOptionProps) =>
  option.value === AUTOCOMPLETE_LOADING_OPTION.value ? (
    <Stack sx={{ justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
      <CircularProgress size={16} />
    </Stack>
  ) : (
    <Stack
      component="li"
      {...rest}
      sx={{
        m: 0.5,
        [`&&.${autocompleteClasses.option}`]: {
          py: !dense && option.description ? 2 : 1,
          borderRadius: theme => `calc(${theme.shape.borderRadius}px / 2)`,
          px: 2,
          alignItems: "flex-start",
        },
        ...sx,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: endAdornment ? "100%" : undefined }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: option.description ? 600 : "revert" }}>
          {option.label}
        </Typography>
        {endAdornment}
      </Stack>
      {option.description && (
        <Typography
          variant="body2"
          sx={{
            fontSize: theme => theme.typography.pxToRem(12),
            color: theme => theme.palette.text.secondary,
            textWrap: "pretty",
          }}
        >
          {option.description}
        </Typography>
      )}
    </Stack>
  );
export const CreateNewAutocompleteOption = (props: AutocompleteOptionProps) => (
  <>
    <Divider />
    <AutocompleteOption {...props} />
  </>
);

export const createFilterAutocompleteOption = createFilterOptions<AutocompleteOptionType>({
  stringify: option => option.label + " " + option.description,
});

export const withCreateNewOption = (
  options: AutocompleteOptionType[],
  inputValue: string,
  t: Translate
): AutocompleteOptionWithMaybeIsNew[] => [
  ...options,
  {
    value: inputValue,
    label: inputValue
      ? t("MultiAutocomplete.CreateNewOption.Label", { inputValue })
      : t("MultiAutocomplete.CreateNewOption.No.Input.Label"),
    isNew: true,
  },
];
